import { combineReducers, createSlice } from '@reduxjs/toolkit';

import disclaimerMessagesReducer from '@polyai/stream/store/reducers/agent-settings/slices/disclaimer-message';
import greetingsReducer from '@polyai/stream/store/reducers/agent-settings/slices/greeting';
import languageBehaviorsReducer from '@polyai/stream/store/reducers/agent-settings/slices/language-behavior';
import personalityReducer from '@polyai/stream/store/reducers/agent-settings/slices/personality';
import roleReducer from '@polyai/stream/store/reducers/agent-settings/slices/role';
import rulesReducer from '@polyai/stream/store/reducers/agent-settings/slices/rules';

import { assignDefinedFields } from '../../utils/assign-fields';
import { assignMetadata } from '../../utils/assign-metadata';
import {
  updateChannelGreeting,
  updateVoiceChannelDisclaimer,
} from '../channels/actions';
import { ChannelType } from '../channels/types';
import { deleteFunction } from '../functions/actions';
import {
  updateDisclaimerMessage,
  updateGreeting,
  updatePersonality,
  updateRole,
  updateRules,
} from './actions';
import {
  greetingReferencesAdapter,
  personalityReferencesAdapter,
  rolesReferencesAdapter,
  rulesReferencesAdapter,
} from './adapters';
import { DEPRECATED_INITIAL_STATE } from './constants';

// Deprecated - refer to multilingualAgentSettingsSliceReducers for the new slice.
const slice = createSlice({
  name: 'agentSettings',
  initialState: DEPRECATED_INITIAL_STATE,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(updateGreeting, (state, action) => {
      assignDefinedFields(state.greeting, action.payload);
      if (action.payload.references !== undefined) {
        state.greeting.references =
          greetingReferencesAdapter.createInitialState(
            action.payload.references,
          );
      }
      assignMetadata(action, state.greeting);
    });

    builder.addCase(updateDisclaimerMessage, (state, action) => {
      assignDefinedFields(state.disclaimerMessage, action.payload);
      assignMetadata(action, state.disclaimerMessage);
    });

    builder.addCase(updatePersonality, (state, action) => {
      if (action.payload.adjectives?.values?.Other) {
        Object.keys(state.personality.adjectives).forEach(
          key => (state.personality.adjectives[key] = false),
        );
      } else if (state.personality.adjectives.Other) {
        state.personality.adjectives.Other = false;
      }

      if (action.payload.adjectives) {
        assignDefinedFields(
          state.personality.adjectives,
          action.payload.adjectives?.values ?? {},
        );
      }

      if (action.payload.custom) {
        state.personality.custom = action.payload.custom ?? '';
      }

      if (action.payload.references !== undefined) {
        state.personality.references =
          personalityReferencesAdapter.createInitialState(
            action.payload.references,
          );
      }
      assignMetadata(action, state.personality);
    });

    builder.addCase(updateRole, (state, action) => {
      assignDefinedFields(state.role, action.payload);
      if (action.payload.references !== undefined) {
        state.role.references = rolesReferencesAdapter.createInitialState(
          action.payload.references,
        );
      }
      assignMetadata(action, state.role);
    });

    builder.addCase(updateRules, (state, action) => {
      assignDefinedFields(state.rules, action.payload);
      if (action.payload.references !== undefined) {
        state.rules.references = rulesReferencesAdapter.createInitialState(
          action.payload.references,
        );
      }
      assignMetadata(action, state.rules);
    });

    // Remove references to the deleted function from all steps in all flows
    builder.addCase(deleteFunction, (state, action) => {
      const { id: functionId } = action.payload;
      if (state.rules.references.globalFunctions[functionId]) {
        delete state.rules.references.globalFunctions[functionId];
      }
      assignMetadata(action, state.rules);
    });

    // ========================================================================
    // Voice channel -> Agent settings syncs
    // ========================================================================
    builder.addCase(updateChannelGreeting, (state, action) => {
      if (
        action.payload.channelType === ChannelType.VOICE &&
        action.payload.greeting
      ) {
        const { greeting } = action.payload;
        assignDefinedFields(state.greeting, greeting);
        if (greeting.references !== undefined) {
          state.greeting.references =
            greetingReferencesAdapter.createInitialState(greeting.references);
        }
        assignMetadata(action, state.greeting);
      }
    });

    builder.addCase(updateVoiceChannelDisclaimer, (state, action) => {
      if (action.payload.disclaimer) {
        const { disclaimer } = action.payload;
        assignDefinedFields(state.disclaimerMessage, disclaimer);
        assignMetadata(action, state.disclaimerMessage);
      }
    });
  },
});

export {
  greetingReferencesAdapter,
  personalityReferencesAdapter,
  rolesReferencesAdapter,
  rulesReferencesAdapter,
};

// This is the reducer for the multilingual agent settings
export const MultilingualAgentSettingsSliceReducers = {
  greetings: greetingsReducer,
  personality: personalityReducer,
  role: roleReducer,
  rules: rulesReducer,
  disclaimerMessages: disclaimerMessagesReducer,
  languageBehaviors: languageBehaviorsReducer,
};

export const multilingualAgentSettingsSliceReducers = combineReducers(
  MultilingualAgentSettingsSliceReducers,
);

export default slice.reducer;
